<template>
    <div class="duplicate-widget">
        <div class="widget-header">
            <h3>{{ widgetTitle }}</h3>
            <div class="svg-container widget-close-button" @click="closeWidget()">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <loading-card :is-loading="isLoading" />
            <div v-if="!isLoading" class="field">
                <label>{{ fieldLabel }}</label>
                <app-input
                    v-model="form.newEntityName"
                    :placeholder="fieldPlaceholder"
                    @blur="$v.form.newEntityName.$touch"
                />
                <span v-if="hasError" class="field-error">
                    {{ fieldErrorMessage }}
                </span>
            </div>
        </div>
        <div class="widget-footer">
            <div class="actions-container">
                <app-button class="transparent-btn" @click="closeWidget">
                    {{ cancelButtonLabel }}
                </app-button>
            </div>
            <div class="actions-container">
                <app-button :disabled="!isFormValid || isLoading" @click="duplicateEntity">
                    {{ primaryButtonLabel }}
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';
import { required } from 'vuelidate/lib/validators';

const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const AppButton = () => import('@/components/common/app-button');
const LoadingCard = () => import('@/components/common/loading-card');
const AppInput = () => import('@/components/common/app-input');

export default {
    name: 'duplicate-widget',

    components: {
        'widget-close-svg': WidgetCloseSvg,
        'app-button': AppButton,
        'loading-card': LoadingCard,
        'app-input': AppInput,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            form: {
                newEntityName: null,
            },

            isLoading: false,
        };
    },

    validations: {
        form: {
            newEntityName: {
                required,
            },
        },
    },

    computed: {
        isFormValid() {
            return !this.$v.$invalid;
        },

        hasError() {
            return this.$v.form.newEntityName.$invalid && this.$v.form.newEntityName.$dirty;
        },

        fieldLabel() {
            return this.widget.fieldLabel || 'Choose a new name for this item';
        },

        fieldPlaceholder() {
            return this.widget.fieldPlaceholder || 'Name';
        },

        widgetTitle() {
            return this.widget.widgetTitle || `Duplicate ${this.widget.entityType || ''}`;
        },

        fieldErrorMessage() {
            return this.widget.fieldErrorMessage || 'This field is required';
        },

        cancelButtonLabel() {
            return this.widget.cancelButtonLabel || 'Cancel';
        },

        primaryButtonLabel() {
            return this.widget.primaryButtonLabel || 'Duplicate';
        },
    },

    created() {
        this.form.newEntityName = `${this.widget.entityName} (Copy)`;
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        duplicateEntity() {
            if (this.widget.isSocialMedia || this.widget.isPartnerSurvey) {
                this.isLoading = true;

                this.widget.onSubmit(this.form).then(() => {
                    this.closeWidget();
                    this.widget.onSuccess();
                }).catch((error) => {
                    this.$helpers.showFailNotification(
                    `An error occurred trying to duplicate the ${this.widget.entityType}`,
                    { error },
                    );
                }).finally(() => {
                    this.isLoading = false;
                });
            } else {
                const params = {
                    [this.widget.replaceEntityField]: this.form.newEntityName,
                };

                this.isLoading = true;

                this.$api
                    .post(`/api/${this.widget.serviceName}/${this.widget.entityId}/Duplicate`, params)
                    .then(() => {
                        this.$helpers.showSuccessNotification(`${this.widget.entityType} duplicated successfully`);
                        this.widget.onSuccess();
                        this.closeWidget();
                    })
                    .catch((error) => {
                        if(this.widget.onError) {
                            this.widget.onError(error);
                        } else {
                            this.$helpers.showFailNotification(
                            `An error occurred trying to duplicate the ${this.widget.entityType}`,
                            { error },
                            );
                        }
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            }
        },
    },
};
</script>

<style>
.duplicate-widget.widget .content-wrapper {
    padding: 30px 35px;
}

.duplicate-widget .loading-screen {
    width: 100%;
    text-align: center;
}

.duplicate-widget .widget-footer .actions-container {
    display: flex;
}

.duplicate-widget .widget-footer .transparent-btn {
    background: transparent;
    color: #877d88;
}

.duplicate-widget .widget-footer .transparent-btn:hover {
    background: #e1e0e5;
}

.duplicate-widget .field {
    margin-bottom: 16px;
    font: normal normal normal 16px/21px Roboto;
}

.duplicate-widget .field label:first-child {
    margin-bottom: 13px;
}

.duplicate-widget .field .field-error {
    margin-top: 5px;
    color: #f30070;
    display: block;
}
</style>
